// Montagem da URI de discagem do softphone (MicroSIP).
//
// O MicroSIP se registra no Windows como handler dos protocolos sip:/tel:/callto:.
// Navegar para `sip:11987654321` faz o Windows abrir o MicroSIP, que disca usando a
// conta SIP ativa dele. O template é configurável por usuário porque algumas PBX exigem
// o domínio (`sip:{numero}@pbx.exemplo.com`) e outras não.

import { normalizePhone, normalizePhoneForSonax } from '@/lib/sonax/normalizePhone';

export const DEFAULT_SIP_TEMPLATE = 'sip:{numero}';

/** Protocolos aceitos no template. Impede que o campo vire vetor para javascript:/data:. */
const ALLOWED_SCHEMES = /^(sips?|tel|callto):/i;

export type SipBuildReason = 'empty' | 'invalid-phone' | 'invalid-template';

/**
 * Um objeto só, com campos opcionais, em vez de união discriminada por `ok: true | false`:
 * o tsconfig do repo roda com `strictNullChecks: false`, e nesse modo o TS não estreita
 * união por literal booleano — `if (!r.ok)` não daria acesso a `r.reason`.
 */
export interface SipBuildResult {
  ok: boolean;
  uri?: string;
  numero?: string;
  reason?: SipBuildReason;
}

/**
 * Valida o template sem precisar de um telefone real. Usado no formulário do Perfil.
 * Regra: precisa começar com um protocolo permitido e conter ao menos um placeholder
 * conhecido — sem placeholder, todo clique discaria para o mesmo número.
 */
export function validateSipTemplate(template: string): boolean {
  const t = (template ?? '').trim();
  if (!t) return false;
  if (!ALLOWED_SCHEMES.test(t)) return false;
  return t.includes('{numero}') || t.includes('{numero_e164}');
}

/**
 * Monta a URI a partir do telefone cru do CRM.
 *
 * Placeholders:
 *   {numero}       DDD + número sem o 55 (o mesmo formato que o webphone Sonax disca hoje)
 *   {numero_e164}  +55DDD... (E.164 completo)
 */
export function buildSipUri(
  raw: string | null | undefined,
  template?: string | null,
): SipBuildResult {
  if (!raw || !raw.trim()) return { ok: false, reason: 'empty' };

  const tpl = (template ?? '').trim() || DEFAULT_SIP_TEMPLATE;
  if (!validateSipTemplate(tpl)) return { ok: false, reason: 'invalid-template' };

  const numero = normalizePhoneForSonax(raw);
  if (!numero) return { ok: false, reason: 'invalid-phone' };

  const canonical = normalizePhone(raw);
  const e164 = canonical ? `+${canonical}` : '';

  // {numero_e164} PRIMEIRO: substituir {numero} antes deixaria "..._e164" pendurado.
  const uri = tpl
    .split('{numero_e164}').join(e164)
    .split('{numero}').join(numero);

  return { ok: true, uri, numero };
}
